import { AfterViewInit, ContentChild, Directive, ElementRef, forwardRef, Inject, Input, ViewChild } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { PageEvent } from "@angular/material/paginator";
import { MatSnackBarRef } from "@angular/material/snack-bar";
import { Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import * as _ from 'lodash';
import { firstValueFrom, Subscription } from "rxjs";
import { isOnline } from "../../common/setup";
import { ListRangeSelectionModeType, PagedList } from "../../model";
import { AbstractExportContextService } from "../../service/abstract-export-context.service";
import { AddButtonContextService, AddButtonResource } from "../../service/add-button-context.service";
import { AppService } from "../../service/app.service";
import { AuthenticationService } from "../../service/authentication.service";
import { PeriodVariable } from "../../service/date-range.service";
import { FieldService } from "../../service/field.service";
import { MobileListWidgetV2Component } from "../../widget/mobile-list-widget-v2/mobile-list-widget-v2.component";
import { SnackbarService } from "../../widget/shared/snackbar.service";
import { WidgetPrintResizeCoordinatorService } from "../../widget/shared/widget-print-resize-coordinator.service";
import { BulkDeleteDialogComponent, BulkDeleteDialogData, BulkDeleteElementType } from "../component/bulk-delete-dialog/bulk-delete-dialog.component";
import { ListSnackbarButton } from "../component/list-snackbar/list-snackbar.component";
import { CustomTableColumn } from "../custom-table";
import { EditTableColumnsDialogComponent } from "../editTableColumnsDialog/edit-table-columns-dialog.component";
import { SearchFieldComponent, SearchFieldInput, SearchFieldInputType, SearchTargetType } from "../search-field/search-field.component";

let nextId = 0;

@Directive({
    host: { 'class': 'list-widget' }
})
export abstract class ListWidgetV2Component<T> implements AfterViewInit {

    @Input() title: string;

    @Input() sort: string[];

    @Input() pageSize: number = 50;

    @Input() query: { property: string, predicate: string, value: any }[];

    @Input() controlsEnabled: boolean = true;

    @Input() advancedSearchAlwaysOpen: boolean;     //DEPRECATED

    @Input() queryFieldRef: string;

    @Input() advancedSearchLayout: string;      //DEPRECATED

    @Input() blockColumns: number = 1;

    @Input() exportEnabled: boolean = true;

    @Input() emptyMessage: string;

    @Input() exportFileName: string;

    @Input() rangeSelectionEnabled: boolean = true;

    @Input() clickOnRowBehaviour: ClickOnRowBehaviour = ClickOnRowBehaviour.OPEN_DETAILS;

    @Input() bulkControlsEnabled: boolean;

    @Input() description: string;

    @Input() id: string;

    @Input() periodRef: string;

    @ViewChild(MobileListWidgetV2Component) protected mobileListWidget: MobileListWidgetV2Component<T>;

    @ContentChild(SearchFieldComponent) searchField: SearchFieldComponent;

    elements: T[];
    dataSource = new MatTableDataSource<T>([]);
    loaded: boolean = false;
    length: number;
    pageIndex: number = 0;
    isMobile: boolean;
    totalPages: number;
    selectedElements: T[] = [];
    isAdvancedSearchOpen: boolean;
    showAddButton: boolean;
    displayedColumns: CustomTableColumn[];
    error: string;
    importTitle: string;
    csvExample: string;
    descriptions: string[] = [];
    readPermission: boolean;
    writePermission: boolean;
    popupSearchTarget: SearchTargetType;
    matPaginatorClass: string;
    preserveSelectedBetweenPages: boolean;
    allElementsSelected: boolean;
    editTableColumnsEnabled: boolean;
    periodRefEnabled: boolean;
    waitForAdvancedSearchInit: boolean;
    initCompleted: boolean;
    searchFieldId: string;
    searchFieldInputs: SearchFieldInput[];
    snackbarButtons: ListSnackbarButton[];
    dividerIndexes: number[];

    // filler loading properties
    fillerRowCount: number = 50;
    fillerRowHeight: number = 10;
    fillerRowHeightWithSpace: number = 12;
    fillerRowOffset: number = 10;

    protected advancedSearchBody: any;
    protected exportId: string;
    protected exportVisibilitySubscription: Subscription;
    protected fieldServiceSubscription: Subscription;
    protected addButtonVisibilitySubscription: Subscription;
    protected columnComponentsArray: any[];
    protected periodFieldSubscription: Subscription;
    protected defaultSearchFieldInputs: SearchFieldInput[] = [
        { type: SearchFieldInputType.KEY, property: null, columnComponents: null },
        { type: SearchFieldInputType.ADVANCED, property: null, columnComponents: null },
        { type: SearchFieldInputType.CLEAR, property: null, columnComponents: null }
    ];
    private periodRefStartTimestamp: number;
    private periodRefEndTimestamp: number;
    private periodFieldRefTimeoutId: any;
    private advancedSearchTimeoutId: any;
    private advancedSearchInitCompleted: boolean;

    protected abstract goToDetail(element: T): void;
    protected abstract refreshList(data?: any): void;
    protected abstract subscribeToExportServices(): void;

    private _unregisterPrintResize: (() => void) | null = null;

    constructor(
        @Inject(forwardRef(() => AppService)) private appService: AppService,
        @Inject(forwardRef(() => AuthenticationService)) protected authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AbstractExportContextService)) protected exportService: AbstractExportContextService,
        @Inject(forwardRef(() => AddButtonContextService)) protected addButtonService: AddButtonContextService,
        @Inject(forwardRef(() => MatDialog)) protected dialog: MatDialog,
        @Inject(forwardRef(() => SnackbarService)) private snackbarService: SnackbarService,
        @Inject(forwardRef(() => FieldService)) protected fieldService: FieldService,
        @Inject(forwardRef(() => ElementRef)) private _listElementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) private _listCoordinator: WidgetPrintResizeCoordinatorService
    ) { }

    ngAfterViewInit(): void {
        this.updatePaginatorClass();
        this._unregisterPrintResize = this._listCoordinator.subscribeElement(this._listElementRef);
    }

    ngOnDestroy(): void {
        this._unregisterPrintResize?.();
        this._unregisterPrintResize = null;
        this.unsubscribeFormExport();
        this.unsubscribeFromAddButtonVisibility();
        this.unsubscribeFromPeriodField();
        this.clearAdvancedSearchTimeout();
    }

    checkIsMobile(): void {
        this.isMobile = this.appService.isMobile();
    }

    refreshElementList(): void {
        this.selectedElements = [];
        this.allElementsSelected = false;
        this.loaded = false;
        this.refreshList();
        this.updateSnackbarButtons();
    }

    updateElementList(pagedList: PagedList<T>, isHistorical?: boolean): void {
        if (this.isMobile) {
            this.mobileListWidget.updateElementList(pagedList);
        } else {
            if (isHistorical) {
                this.fillerRowCount = pagedList.numberOfElements;
                this.length = (pagedList.number * pagedList.size) + pagedList.numberOfElements + (pagedList.last ? 0 : 1);
            } else {
                this.fillerRowCount = Math.min(pagedList.size, pagedList.totalElements);
                this.length = pagedList.totalElements;
            }
            if (this.fillerRowCount == 0) { // set 5 rows if no rows
                this.fillerRowCount = 5;
            }
            this.elements = pagedList.content;
            this.dataSource = new MatTableDataSource<T>(this.elements);
            this.pageSize = pagedList.size;
            this.pageIndex = pagedList.number;
            this.totalPages = pagedList.totalPages;
            this.loaded = true;
        }
    }

    changePage(pageEvent: PageEvent): void {
        this.pageIndex = pageEvent.pageIndex;
        if (this.preserveSelectedBetweenPages) {
            if (this.allElementsSelected) {
                this.allElementsSelected = false;
                this.selectedElements = [];
            }
            this.loaded = false;
            this.refreshList();
        } else {
            this.refreshElementList();
        }
        this.updateSnackbarButtons();
    }

    changeSort(sort: Sort): void {
        this.sort = [sort.active, sort.direction];
        this.refreshElementList();
        this.updateSnackbarButtons();
    }

    protected executeAdvancedSearch(advancedSearchBody: any): void {
        this.advancedSearchBody = advancedSearchBody;
        this.pageIndex = 0;
        if (this.periodRefEnabled) {
            this.advancedSearchBody = this.advancedSearchBody || {};
            if (!(this.query && this.query.some(el => el.property == 'startTimestamp'))) {
                this.advancedSearchBody.startTimestamp = this.periodRefStartTimestamp;
            }
            if (!(this.query && this.query.some(el => el.property == 'endTimestamp'))) {
                this.advancedSearchBody.endTimestamp = this.periodRefEndTimestamp;
            }
            if (this.waitForAdvancedSearchInit && !this.advancedSearchInitCompleted) {
                this.advancedSearchInitCompleted = true;
            } else {
                this.refreshElementList();

            }
        } else {
            this.refreshElementList();
        }
    }

    updateSelectedElements(elements: T[]): void {
        this.allElementsSelected = false;
        this.selectedElements = _.cloneDeep(elements);
        this.updateSnackbarButtons();
    }

    updateAdvancedSearchStatus(isOpen: boolean): void {
        this.isAdvancedSearchOpen = isOpen;
    }

    protected updatePaginatorClass(): void {
        if (this.authenticationService.getTenant().listRangeSelectionMode == ListRangeSelectionModeType.PAGES) {
            this.matPaginatorClass = "mat-paginator-pages-mode";
        }
    }

    protected getNextId(): number {
        return nextId++;
    }

    protected subscribeToExportVisibility(): void {
        this.exportVisibilitySubscription = this.exportService.getIsExportButtonPresent().subscribe(isExportButtonPresent => {
            this.exportEnabled = !isExportButtonPresent;
        });
    }

    protected unsubscribeFormExport(): void {
        if (this.exportId) {
            this.exportService.unsubscribeFromExport(this.exportId);
        }
        if (this.exportVisibilitySubscription) {
            this.exportVisibilitySubscription.unsubscribe();
        }
    }

    setAllElementsSelected(): void {
        this.allElementsSelected = true;
        this.selectedElements = _.cloneDeep(this.elements);
        this.updateSnackbarButtons();
    }

    clearSelectedElements(): void {
        this.allElementsSelected = false;
        this.selectedElements = [];
        this.updateSnackbarButtons();
    }

    protected subscribeToAddButtonVisibility(resource: AddButtonResource): void {
        this.addButtonVisibilitySubscription = this.addButtonService.getIsAddButtonPresent(resource).subscribe(isAddButtonPresent => {
            this.showAddButton = !isAddButtonPresent;
        });
    }

    protected unsubscribeFromAddButtonVisibility(): void {
        if (this.addButtonVisibilitySubscription) {
            this.addButtonVisibilitySubscription.unsubscribe();
        }
    }

    protected openBulkDeleteDialog(searchParams: any, elementType: BulkDeleteElementType): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.minWidth = '25%';
        const data: BulkDeleteDialogData = {
            selectedElementIds: this.selectedElements.map(el => { return el["id"]; }),
            allElementsSelected: this.allElementsSelected,
            searchParams: searchParams,
            elementCount: this.allElementsSelected ? this.length : this.selectedElements.length,
            elementType: elementType
        }
        dialogConfig.data = data;
        firstValueFrom(this.dialog.open(BulkDeleteDialogComponent, dialogConfig).afterClosed()).then(result => {
            if (result) {
                this.showSnackbar('bulkDeleteExecutedProperty');
                this.refreshElementList();
            }
        });
    }

    protected showSnackbar(text: string, panelClass?: string, translateText: boolean = true, actionLabel?: string): Promise<MatSnackBarRef<any>> {
        return this.snackbarService.showSnackbar(text, panelClass, translateText, actionLabel);
    }

    protected getSelectedElementIds(): string[] {
        return this.selectedElements.map(el => { return el["id"]; });
    }

    openEditTableColumnsDialog(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.maxWidth = '428px';
        dialogConfig.data = {
            localStorageId: this.id,
            columnComponent: this.columnComponentsArray
        }
        dialogConfig.autoFocus = false;
        firstValueFrom(this.dialog.open(EditTableColumnsDialogComponent, dialogConfig).afterClosed()).then(refresh => {
            if (refresh) {
                this.refreshAfterColumnChanges();
            }
        });
    }

    protected refreshAfterColumnChanges(): void {
        this.initVisibleColumns();
        this.refreshElementList();
    }

    protected initVisibleColumns(): void {
        /* do nothing */
    }

    protected subscribeToPeriodField(): void {
        if (this.periodRef) {
            this.periodRefEnabled = true;
            this.periodFieldRefTimeoutId = setTimeout(() => {
                console.error("Unresolved periodRef");
                this.waitForAdvancedSearchInitAndPerformLoadChartData();
            }, 5000);
            this.periodFieldSubscription = this.fieldService.subscribeToFields([this.periodRef]).subscribe(fieldsMap => {
                const period: PeriodVariable = fieldsMap ? fieldsMap[this.periodRef] : null;
                if (period != null) {
                    this.clearPeriodFieldRefTimeout();
                    this.periodRefStartTimestamp = period?.start;
                    this.periodRefEndTimestamp = period?.end;
                    this.waitForAdvancedSearchInitAndPerformLoadChartData();
                }
            });
        }
    }

    private waitForAdvancedSearchInitAndPerformLoadChartData(): void {
        if (this.waitForAdvancedSearchInit) {
            this.clearAdvancedSearchTimeout();
            if (this.advancedSearchInitCompleted) {
                this.executeAdvancedSearch(this.advancedSearchBody);
            } else {
                this.advancedSearchTimeoutId = setTimeout(() => this.waitForAdvancedSearchInitAndPerformLoadChartData(), 50);
            }
        } else {
            this.executeAdvancedSearch(this.advancedSearchBody);
        }
    }

    private clearPeriodFieldRefTimeout(): void {
        if (this.periodFieldRefTimeoutId) {
            clearTimeout(this.periodFieldRefTimeoutId);
            this.periodFieldRefTimeoutId = null;
        }
    }

    private clearAdvancedSearchTimeout(): void {
        if (this.advancedSearchTimeoutId) {
            clearTimeout(this.advancedSearchTimeoutId);
            this.advancedSearchTimeoutId = null;
        }
    }

    protected unsubscribeFromPeriodField(): void {
        if (this.periodRefEnabled) {
            this.fieldService.unsubscribeFromFields([this.periodRef]);
            this.periodFieldSubscription.unsubscribe();
        }
        this.clearPeriodFieldRefTimeout();
    }

    protected isOnline(): boolean {
        return isOnline();
    }

    protected getSearchFieldInputs(): void {
        this.searchFieldInputs = this.searchField?.inputs || this.defaultSearchFieldInputs;
    }

    protected updateSnackbarButtons(): void {
        /* do nothing */
    }

}

export class BulkControl {
    icon: string;
    iconClass: string;
    action: string;
    title: string;
    visible: boolean;
    disabled?: boolean;
}

export enum ClickOnRowBehaviour {
    OPEN_DETAILS = "OPEN_DETAILS",
    OPEN_CONTEXT_OBJECT = "OPEN_CONTEXT_OBJECT",
    SELECT_ELEMENT = "SELECT_ELEMENT"
}

export enum ColumnComponentDisplayMode {
    VISIBLE = "VISIBLE",
    INITIALLY_HIDDEN = "INITIALLY_HIDDEN",
    ALWAYS_HIDDEN = "ALWAYS_HIDDEN"
}

export enum DetailsModeType {
    PAGE = "PAGE",
    POPUP = "POPUP",
    NONE = "NONE"
}
